import { useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ELITE_PLANS_ELITE_PRIME_TAB_ID,
  ELITE_PLANS_ELITE_TAB_ID,
  useElitePlansLanding
} from '../../ElitePlansLandingContext'
import { useElitePlansFloatingCta } from '../../ElitePlansFloatingCtaContext'
import { ElitePlansHeroStatusSkeleton } from './ElitePlansHeroStatusSkeleton'
import { buildElitePlansAdvisoryCtaPayload } from '../../elitePlansAdvisorySelection'
import { buildElitePlansCtaPayload } from '../../elitePlansLandingState'
import {
  resolveElitePlansPrimaryCtaDisplay,
  resolveElitePlansPriceBasedOfferDisplay,
  resolveElitePlansTenureBasedOfferDisplay,
  resolveRecommendedElitePrimePlan
} from '../../elitePlansPrimaryCtaDisplay'
import {
  buildElitePlansUpgradeCtaPayload,
  resolveElitePrimeUpgradeDetails,
  type ElitePrimeUpgradeDetails
} from '../../elitePlansUpgradeDetails'
import { getEliteBuyCtaEventExtras } from '../../../../utils/eliteEventExtras'
import {
  resolvePlanId,
  resolveSelectedPlanForCta
} from '../../../new-elite-landing/components/NewEliteLandingPlanSelector'
import NewEliteLandingTrustMetrics from '../../../new-elite-landing/components/NewEliteLandingTrustMetrics'
import { resolveElitePlansPlusBanner } from '../../elitePlansPlusBannerDisplay'
import { ElitePlansTenureSelector } from '../ElitePlansTenureSelector'
import {
  ElitePlansHeroEmblem,
  ElitePlansHeroBadge,
  ElitePlansHeroHeadline,
  ElitePlansHeroSubcopy
} from './ElitePlansHeroHeading'
import { ElitePlansHeroPrimaryCta } from './ctas/ElitePlansHeroPrimaryCta'
import { ElitePlansUpgradeAdjustmentNote } from './ctas/ElitePlansUpgradeAdjustmentNote'
import { ElitePlansConsentRow } from './ElitePlansConsentRow'
import { ElitePlansSupportText } from './ElitePlansSupportText'
import { ElitePlansPlusBanner } from './plus-banner/ElitePlansPlusBanner'
import { ElitePlansLockInPriceBanner } from './lock-in-price/ElitePlansLockInPriceBanner'
import { getEligiblePlanCtaAmount } from '../../../../components/elite/dashboard/eliteOnboardingFocusPlan'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { ElitePlansHeroCurve } from '../../icons/ElitePlansHeroCurve'
import { useElitePlansIsDarkMode } from '../../hooks/useElitePlansIsDarkMode'
import { postCTA as postCta } from '../../../../utils/appBridge'
import { grantElitePrimeConsent } from '../../../../repository/misc'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import type { EliteLandingPlan } from '../../../new-elite-landing/planTypes'
import { EliteLandingPageState, type EliteOnboardingStatusResponse } from '../../../../types/misc.types'
import VaulDrawer from '../../../../components/BottomSheetComponents/VaulDrawer'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { buildFamilyOnboardingViewProps } from '../../../new-elite-landing/pages/family/familyOnboardingViewProps'
import {
  resolveIsFamilyBlocked,
  resolveIsFamilyPrimePurchaseBlocked
} from '../../elitePlansMembership'
import {
  ELITE_FAMILY_BLOCKED_CTA_LABEL,
  ELITE_FAMILY_BLOCKED_NOTE,
  ELITE_FEE_SUFFIX_MONTHLY,
  ELITE_FEE_SUFFIX_YEARLY,
  ELITE_PRIME_FAMILY_BLOCKED_CTA_LABEL,
  ELITE_PRIME_FAMILY_BLOCKED_NOTE
} from '../../constants/ctaCopy'

export function ElitePlansHero() {
  const { activeTab, lifecycle, status, eliteAdvisory, isStatusLoading } = useElitePlansLanding()
  const floatingCta = useElitePlansFloatingCta()
  const isDarkMode = useElitePlansIsDarkMode()
  const plusBanner = resolveElitePlansPlusBanner(activeTab.plusBanner, status)
  const plan = plusBanner ? { ...activeTab, plusBanner } : activeTab
  const themeId = plan.themeId
  const [isConsentChecked, setIsConsentChecked] = useState(true)
  const [isEliteEsignSheetOpen, setIsEliteEsignSheetOpen] = useState(false)
  const eliteEsignSheetTitleId = useId()
  const [isGrantingPrimeConsent, setIsGrantingPrimeConsent] = useState(false)
  const { showToast } = useToast()
  const usesAdvisoryFlow = plan.id === ELITE_PLANS_ELITE_TAB_ID && eliteAdvisory !== null
  const showConsent = usesAdvisoryFlow ? eliteAdvisory.showConsent : lifecycle.showConsent
  const isCtaDisabledByState = usesAdvisoryFlow
    ? eliteAdvisory.isCtaDisabledByState
    : lifecycle.isCtaDisabledByState
  const familyConfig = buildFamilyOnboardingViewProps(status)
  const isElitePrimeTab = plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID
  // Elite: only FAMILY_ONBOARDING_NOT_ALLOWED. Prime: also primary-free + secondary-Elite.
  const isFamilyBlocked = isElitePrimeTab
    ? resolveIsFamilyPrimePurchaseBlocked(status)
    : resolveIsFamilyBlocked(status)
  const isCtaDisabled =
    isCtaDisabledByState ||
    isFamilyBlocked ||
    (showConsent && !isConsentChecked) ||
    isGrantingPrimeConsent
  // No sticky buy bar while family purchase is blocked (same as NewEliteLandingPlanSelector)
  const showFloatingPrimaryCta =
    Boolean(floatingCta?.enableFloatingCta) &&
    Boolean(floatingCta?.isFloatingCtaVisible) &&
    !isStatusLoading &&
    !isFamilyBlocked
  const supportText = usesAdvisoryFlow ? eliteAdvisory.supportText : lifecycle.supportText
  const consentTextList = (usesAdvisoryFlow ? eliteAdvisory.consentTextList : lifecycle.consentTextList) ?? []
  const landingPageState = usesAdvisoryFlow
    ? eliteAdvisory.landingPageState
    : lifecycle.landingPageState
  const elitePrimeUpgrade =
    plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID ? resolveElitePrimeUpgradeDetails(status) : null
  const primaryCtaDisplay = resolveElitePlansPrimaryCtaDisplay({
    plan,
    status,
    lifecycle,
    advisory: eliteAdvisory
  })
  const priceBasedOffer =
    plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID && !elitePrimeUpgrade
      ? resolveElitePlansPriceBasedOfferDisplay(status)
      : null
  const tenureBasedOffer =
    plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID && !elitePrimeUpgrade && !priceBasedOffer
      ? resolveElitePlansTenureBasedOfferDisplay(status)
      : null

  const primaryCtaPayload = resolveElitePlansPrimaryCtaPayload({
    elitePrimeUpgrade,
    usesAdvisoryFlow,
    status,
    advisoryLiveCta: usesAdvisoryFlow ? eliteAdvisory.liveCta : undefined,
    selectedTenure: usesAdvisoryFlow ? eliteAdvisory.selectedTenure : undefined,
    lifecycleLiveCta: lifecycle.liveCta
  })

  const isEliteEsignPending =
    isElitePrimeTab && landingPageState === EliteLandingPageState.ELITE_PAYMENT_COMPLETED_PRIME_PENDING

  const handlePrimaryCtaActivate = async () => {
    // Family blocked: CTA stays disabled — never post buy for secondary
    if (isFamilyBlocked) return
    // Prime upgrade is blocked until the paid Elite plan is eSigned — explain instead of posting
    if (isEliteEsignPending) {
      setIsEliteEsignSheetOpen(true)
      return
    }
    if (!primaryCtaPayload) return
    // Prime consent must be recorded before the CTA runs; a failed grant blocks the CTA
    if (plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID && status?.isPrimeConsent) {
      setIsGrantingPrimeConsent(true)
      try {
        await grantElitePrimeConsent()
      } catch {
        showToast({ message: 'Something went wrong. Please try again.', duration: 2500 })
        return
      } finally {
        setIsGrantingPrimeConsent(false)
      }
    }
    postCta(JSON.stringify(primaryCtaPayload))
  }

  const buyCtaEventExtras = getEliteBuyCtaEventExtras(status)
  const analyticsPlanId = resolveElitePlansAnalyticsPlanId({
    planTabId: plan.id,
    status,
    elitePrimeUpgrade,
    usesAdvisoryFlow,
    selectedTenure: usesAdvisoryFlow ? eliteAdvisory.selectedTenure : undefined
  })

  return (
    <section className='ep-hero-zone relative text-center'>
      <div className='ep-page-bg ep-theme-tween w-full'>
        <div className='relative mx-auto flex max-w-[720px] flex-col items-center px-5 pb-8'>
          <ElitePlansHeroEmblem
            riveSrc={isDarkMode ? plan.hero.emblemRiveDarkSrc : plan.hero.emblemRiveSrc}
            fallbackSrc={plan.hero.emblemSrc}
            alt={plan.hero.emblemAlt ?? ''}
          />
          {plan.hero.badge ? <ElitePlansHeroBadge>{plan.hero.badge}</ElitePlansHeroBadge> : null}
          <ElitePlansHeroHeadline
            headlineSans={plan.hero.headlineSans}
            headlineSerif={plan.hero.headlineSerif}
          />
          <ElitePlansHeroSubcopy>{plan.hero.subcopy}</ElitePlansHeroSubcopy>
          <NewEliteLandingTrustMetrics
            metrics={plan.trustItems}
            className='mt-6 mb-0 pt-0'
            hideIcons
            showDividers
            valueClassName='text-[color:var(--ep-metric-value)]'
            labelClassName='text-[color:var(--ep-metric-label)]'
          />
          {isStatusLoading ? (
            <ElitePlansHeroStatusSkeleton
              showTenure={plan.id === ELITE_PLANS_ELITE_TAB_ID}
              showPlusBanner={Boolean(plan.plusBanner)}
              showLockInBanner={Boolean(plan.lockInPriceBanner)}
            />
          ) : (
            <>
              {usesAdvisoryFlow && !isFamilyBlocked ? (
                <ElitePlansTenureSelector advisory={eliteAdvisory} planId={plan.id} />
              ) : null}
              <div ref={floatingCta?.inlineCtaRef} className='w-full'>
                <ElitePlansHeroPrimaryCta
                  priceBasedOffer={priceBasedOffer}
                  tenureBasedOffer={tenureBasedOffer}
                  familyBlockedNote={
                    isFamilyBlocked
                      ? resolveFamilyBlockedNote(isElitePrimeTab, familyConfig.secondaryUserName)
                      : ''
                  }
                  display={
                    isFamilyBlocked
                      ? { variant: 'plain', label: resolveFamilyBlockedCtaLabel(isElitePrimeTab) }
                      : primaryCtaDisplay
                  }
                  planId={analyticsPlanId}
                  disabled={isCtaDisabled}
                  landingPageState={landingPageState}
                  eventExtras={buyCtaEventExtras}
                  onActivate={handlePrimaryCtaActivate}
                />
              </div>
              {isFamilyBlocked ? null : (
                <>
                  {elitePrimeUpgrade?.upgradePricingDetails ? (
                    <ElitePlansUpgradeAdjustmentNote
                      adjustmentFromCurrentPlan={
                        elitePrimeUpgrade.upgradePricingDetails.adjustmentFromCurrentPlan
                      }
                    />
                  ) : null}
                  {supportText ? <ElitePlansSupportText supportText={supportText} planId={plan.id} /> : null}
                  {showConsent ? (
                    <ElitePlansConsentRow
                      planId={plan.id}
                      consentTextList={consentTextList}
                      checked={isConsentChecked}
                      onCheckedChange={setIsConsentChecked}
                    />
                  ) : null}
                  {plan.id === ELITE_PLANS_ELITE_TAB_ID && plan.lockInPriceBanner ? (
                    <ElitePlansLockInPriceBanner
                      planId={plan.id}
                      banner={plan.lockInPriceBanner}
                      {...resolveEliteLockInPriceDisplay(
                        usesAdvisoryFlow ? eliteAdvisory.selectedTenure : undefined,
                        status
                      )}
                    />
                  ) : null}
                </>
              )}
              {plan.plusBanner ? (
                <ElitePlansPlusBanner planId={plan.id} plusBanner={plan.plusBanner} />
              ) : null}
            </>
          )}
        </div>

        <ElitePlansHeroCurve themeId={themeId} />
      </div>

      {/* Portal escapes the tabpanel motion transform so `fixed` sticks to the viewport.
          Host itself is fixed + z-50 (same body-level stacking as the About FAB) so overflow
          scroll layers in CardsTrustMarker cannot paint over the bar. */}
      {floatingCta?.enableFloatingCta
        ? createPortal(
          // Theme tokens (--ep-*) live on .elite-plans-root[data-plan]; keep them on the portal host
          <div
            className='elite-plans-root pointer-events-none fixed inset-x-0 z-50 mx-auto w-full max-w-[420px] px-5'
            data-plan={themeId}
            data-display-mode={isDarkMode ? 'dark' : 'light'}
            style={{ bottom: `${getBottomPadding() + 16}px`, background: 'transparent' }}
          >
            <div className='ep-hero-zone'>
              <AnimatePresence>
                {showFloatingPrimaryCta ? (
                  <motion.div
                    key='elite-plans-floating-primary-cta'
                    ref={floatingCta.floatingCtaRef}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className='pointer-events-auto'
                  >
                    <ElitePlansHeroPrimaryCta
                      priceBasedOffer={null}
                      tenureBasedOffer={null}
                      familyBlockedNote=''
                      display={
                        isFamilyBlocked
                          ? { variant: 'plain', label: resolveFamilyBlockedCtaLabel(isElitePrimeTab) }
                          : primaryCtaDisplay
                      }
                      planId={analyticsPlanId}
                      disabled={isCtaDisabled}
                      landingPageState={landingPageState}
                      eventExtras={buyCtaEventExtras}
                      onActivate={handlePrimaryCtaActivate}
                      className='mt-0'
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>,
          document.body
        )
        : null}

      {isEliteEsignPending ? (
        <VaulDrawer
          open={isEliteEsignSheetOpen}
          setOpen={setIsEliteEsignSheetOpen}
          titleId={eliteEsignSheetTitleId}
          focusDrawerOnOpen
        >
          <EliteEsignPendingSheet
            titleId={eliteEsignSheetTitleId}
            onClose={() => setIsEliteEsignSheetOpen(false)}
          />
        </VaulDrawer>
      ) : null}
    </section>
  )
}

function EliteEsignPendingSheet({ titleId, onClose }: { titleId: string; onClose: () => void }) {
  return (
    <div className='bg-white px-5 pt-8 text-left' style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}>
      <h2 id={titleId} tabIndex={-1} className='text-xl font-medium leading-[1.3] text-[#232323] outline-none'>
        Complete Elite eSign first to upgrade to Prime
      </h2>
      <p className='mt-3 text-13 leading-[1.5] text-grey40'>
        Elite membership is activated only after you complete the eSign agreement. Once active, you can proceed
        to upgrade to Elite Prime.
      </p>
      <button
        type='button'
        onClick={onClose}
        className='ep-focus-ring mt-6 flex min-h-[56px] w-full items-center justify-center rounded-2xl border-none bg-grey10 px-5 text-[15px] font-medium text-white'
      >
        Close
      </button>
    </div>
  )
}

function resolveElitePlansPrimaryCtaPayload({
  elitePrimeUpgrade,
  usesAdvisoryFlow,
  status,
  advisoryLiveCta,
  selectedTenure,
  lifecycleLiveCta
}: {
  elitePrimeUpgrade: ElitePrimeUpgradeDetails | null
  usesAdvisoryFlow: boolean
  status?: EliteOnboardingStatusResponse
  advisoryLiveCta?: EliteOnboardingStatusResponse['cta']
  selectedTenure?: EliteLandingPlan
  lifecycleLiveCta?: EliteOnboardingStatusResponse['cta']
}): Record<string, unknown> | null {
  if (elitePrimeUpgrade) {
    return buildElitePlansUpgradeCtaPayload(elitePrimeUpgrade)
  }
  if (usesAdvisoryFlow && selectedTenure) {
    return buildElitePlansAdvisoryCtaPayload(status, advisoryLiveCta, selectedTenure)
  }
  return buildElitePlansCtaPayload(status, lifecycleLiveCta)
}

/** Analytics `planId`: effective plan id from the CTA source, not the tab id (`elite` / `elite-prime`). */
function resolveElitePlansAnalyticsPlanId({
  planTabId,
  status,
  elitePrimeUpgrade,
  usesAdvisoryFlow,
  selectedTenure
}: {
  planTabId: string
  status?: EliteOnboardingStatusResponse
  elitePrimeUpgrade: ElitePrimeUpgradeDetails | null
  usesAdvisoryFlow: boolean
  selectedTenure?: EliteLandingPlan
}): string {
  if (usesAdvisoryFlow && selectedTenure) {
    const selectedPlanId = resolvePlanId(
      resolveSelectedPlanForCta(status?.eligiblePlans ?? [], selectedTenure)
    )
    if (selectedPlanId) return selectedPlanId
  }

  if (elitePrimeUpgrade) {
    const upgradePlanId =
      elitePrimeUpgrade.upgradePlanDetails?.effectivePlanId ??
      elitePrimeUpgrade.upgradePlanDetails?.basePlanId
    if (upgradePlanId) return upgradePlanId
  }

  const recommendedPlanId = resolvePlanId(resolveRecommendedElitePrimePlan(status))
  if (recommendedPlanId) return recommendedPlanId

  return planTabId
}

function resolveFamilyBlockedNote(isElitePrimeTab: boolean, familyMemberName: string): string {
  const note = isElitePrimeTab ? ELITE_PRIME_FAMILY_BLOCKED_NOTE : ELITE_FAMILY_BLOCKED_NOTE
  return familyMemberName ? `${note} for ${familyMemberName}` : note
}

function resolveFamilyBlockedCtaLabel(isElitePrimeTab: boolean): string {
  return isElitePrimeTab ? ELITE_PRIME_FAMILY_BLOCKED_CTA_LABEL : ELITE_FAMILY_BLOCKED_CTA_LABEL
}

/** Live fees for the lock-in card + Elite pricing-details sheet headline. */
function resolveEliteLockInPriceDisplay(
  selectedTenure: EliteLandingPlan | undefined,
  status?: EliteOnboardingStatusResponse
): {
  priceLabel: string
  sheetPrice: string
  sheetPriceSuffix: string
  feeAmount: number
} {
  if (!selectedTenure) {
    return { priceLabel: '', sheetPrice: '', sheetPriceSuffix: '', feeAmount: 0 }
  }
  const eligiblePlan = resolveSelectedPlanForCta(status?.eligiblePlans ?? [], selectedTenure)
  const feeAmount = getEligiblePlanCtaAmount(eligiblePlan)
  if (feeAmount <= 0) {
    return { priceLabel: '', sheetPrice: '', sheetPriceSuffix: '', feeAmount: 0 }
  }
  const sheetPrice = `${roundOffAndFormatNumberWithCurrency(feeAmount)}`
  const cardSuffix = selectedTenure === 'yearly' ? '/yr' : '/mo'
  const sheetPriceSuffix =
    selectedTenure === 'yearly' ? ELITE_FEE_SUFFIX_YEARLY : ELITE_FEE_SUFFIX_MONTHLY
  return {
    priceLabel: `${sheetPrice}${cardSuffix}`,
    sheetPrice,
    sheetPriceSuffix,
    feeAmount
  }
}
